'use client';

// Track return focus targets by key
const modalReturnTargets = new Map<string, React.RefObject<HTMLElement | null> | null>();

// Track the last clicked trigger element (for auto-coordination)
let lastClickedTriggerRef: React.RefObject<HTMLElement | null> | null = null;

/**
 * Specify where focus should return when a modal closes. Call this before programmatically
 * opening a modal to override the default focus restoration behavior.
 *
 * @param modalKey - Unique key for the modal (must match Modal's returnFocusKey prop)
 * @param focusTarget - Ref to the element that should receive focus when modal closes
 *
 * @example
 * ```tsx
 * const inviteButtonRef = useRef<HTMLButtonElement>(null);
 *
 * // In store or event handler
 * const openModal = (returnFocusRef?: React.RefObject<HTMLElement | null>) => {
 *   if (returnFocusRef) {
 *     specifyModalReturnFocus('myModal', returnFocusRef);
 *   }
 *   setModalOpen(true);
 * };
 *
 * // Auto-open with explicit return focus
 * useEffect(() => {
 *   if (shouldAutoOpen) {
 *     specifyModalReturnFocus('myModal', inviteButtonRef);
 *     setModalOpen(true);
 *   }
 * }, [shouldAutoOpen]);
 * ```
 */
export const specifyModalReturnFocus = (modalKey: string, focusTarget: React.RefObject<HTMLElement | null> | null) => {
  modalReturnTargets.set(modalKey, focusTarget);
};

/**
 * Track the last clicked trigger for automatic focus restoration.
 * Used internally by ControlledModalTrigger.
 * @internal
 */
export const trackLastClickedTrigger = (triggerRef: React.RefObject<HTMLElement | null>) => {
  lastClickedTriggerRef = triggerRef;
};

/**
 * Get the return focus target for a modal. Used internally by Modal component.
 * @internal
 */
export const getModalReturnTarget = (key: string | undefined) => {
  const keyedTarget = key ? modalReturnTargets.get(key) : null;
  return keyedTarget ?? lastClickedTriggerRef;
};

/**
 * Clear the return focus target for a modal. Used internally by Modal component.
 * Removes both the keyed Map entry (if provided) and the global lastClickedTriggerRef
 * to prevent stale references from affecting subsequent modal interactions.
 * @internal
 */
export const clearModalReturnTarget = (key: string | undefined) => {
  if (key) {
    modalReturnTargets.delete(key);
  }
  lastClickedTriggerRef = null;
};

/**
 * Check if a specific trigger ref is the active trigger for a modal.
 * Used internally by ControlledModalTrigger for aria-expanded.
 * @internal
 */
export const isActiveTrigger = (triggerRef: React.RefObject<HTMLElement | null>, key: string | undefined): boolean => {
  const activeTarget = getModalReturnTarget(key);
  return activeTarget === triggerRef;
};
